/**
 * Sistema de base de dados em localStorage
 * VERSÃO MULTI-USUÁRIO — ISOLAMENTO COMPLETO
 * Cada usuário tem sua própria base de dados isolada
 *
 * ============================================================
 * 🛡️  VERSÃO ULTRA-ROBUSTA — MELHORIAS APLICADAS
 *
 *  1. TOMBSTONE FILTER em TODOS os getters:
 *       getProperties(), getClients(), getTasks(),
 *       getDocuments(), getMessages(), getCalendarEvents(),
 *       getFinanceiro()
 *     → Itens deletados NUNCA ressuscitam após reload.
 *
 *  2. _writeTombstone(id, collection) — todo método delete*()
 *     grava o ID no localStorage no mesmo formato que o
 *     LocalStrategy V8+ usa, garantindo consistência mesmo
 *     sem o DualSyncStrategy em execução.
 *
 *  3. _getTombstones(collection) — lê tombstones de 3 formatos
 *     (novo, legado, DualSync) com cache in-memory TTL 30 s.
 *
 *  4. _isDeleted(id, collection) — verificação O(1) via Set.
 *
 *  5. _filterTombstoned(items, collection) — filtro centralizado
 *     usado por todos os getters.
 *
 *  6. saveDatabase() com quota guard — captura QuotaExceededError
 *     e dispara evento 'storage:quota_exceeded'.
 *
 *  7. getDatabase() com JSON.parse protegido — JSON corrompido
 *     retorna estrutura vazia sem travar a app.
 *
 *  8. _defaultCompany() — helper centralizado.
 *
 *  9. getDashboardStats() aplica tombstone filter.
 *
 * 10. cleanupTestData() corrigido para não usar índice estático
 *     durante remoção de chaves (evita skip de itens).
 * ============================================================
 */

// 🔥 FIRESTORE IMPORTS (ENTERPRISE)
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import bcrypt from "bcryptjs";

// ============================================================
// 🚀 MIGRAÇÃO AUTOMÁTICA AO CARREGAR O MÓDULO
// ============================================================

const migrateUsersToGlobalDb = () => {
  const sharedDbJson = localStorage.getItem("imoveispro_db");
  if (!sharedDbJson) return;
  let sharedDb;
  try { sharedDb = JSON.parse(sharedDbJson); } catch (_) { return; }
  if (localStorage.getItem("imoveispro_users_db")) return;
  if (sharedDb.users && sharedDb.users.length > 0) {
    localStorage.setItem(
      "imoveispro_users_db",
      JSON.stringify({ users: sharedDb.users })
    );
    console.log("✅ Usuarios migrados a DB global:", sharedDb.users.length, "usuarios");
  }
};

const migrateUserDataToIsolatedDb = (userId) => {
  const sharedDbJson = localStorage.getItem("imoveispro_db");
  if (!sharedDbJson) return false;
  let sharedDb;
  try { sharedDb = JSON.parse(sharedDbJson); } catch (_) { return false; }

  const dbKey = `imoveispro_db_user_${userId}`;
  if (localStorage.getItem(dbKey)) return false;

  const userIdPatterns = [userId, `user_${userId}`, `company_${userId}`];
  const match = (item) =>
    userIdPatterns.includes(item.userId) ||
    (userId === "1" && (item.userId === "1" || !item.userId));

  const userData = {
    clients:       (sharedDb.clients       || []).filter(match),
    properties:    (sharedDb.properties    || []).filter(match),
    tasks:         (sharedDb.tasks         || []).filter(match),
    calendarEvents:(sharedDb.calendarEvents|| []).filter(match),
    documents:     (sharedDb.documents     || []).filter(match),
    financeiro:    (sharedDb.financeiro    || []).filter(match),
    company: sharedDb.company || {
      nome:"",logo:"",cnpj:"",endereco:"",telefone:"",email:"",cor:"#2563eb",
    },
    userPreferences: (sharedDb.userPreferences||{})[userId]
      ? { [userId]: sharedDb.userPreferences[userId] } : {},
  };

  if (userData.clients.length > 0 || userData.properties.length > 0) {
    localStorage.setItem(dbKey, JSON.stringify(userData));
    console.log(`✅ Dados migrados para usuário ${userId}:`, {
      clients: userData.clients.length,
      properties: userData.properties.length,
      tasks: userData.tasks.length,
    });
    return true;
  }
  return false;
};

migrateUsersToGlobalDb();

// Instâncias globais por usuário
const databaseInstances = {};
// ============================================================
// 🔥 DATACONTEXT INTEGRATION (ENTERPRISE OFFLINE-FIRST)
// ============================================================
let _dataContextInstance = null;
const _getDataContext = () => {
  if (_dataContextInstance) return _dataContextInstance;
  try {
    const DataContext = require('../lib/data/context/DataContext').default;
    _dataContextInstance = DataContext.getDataContext();
    if (_dataContextInstance) {
      console.log('🔗 database.js conectado a DataContext (tombstone filtering activo)');
    }
  } catch (e) {
    // DataContext no disponible aún, se usará fallback legacy
  }
  return _dataContextInstance;
};
// ============================================================
// 🛡️  TOMBSTONE HELPERS (módulo-nível, sem estado)
// ============================================================

/**
 * Lê tombstones do localStorage suportando 3 formatos:
 *   1. Novo    → tombstones_${userId}_${collection}  (array de strings)
 *   2. Legado  → imoveispro_tombstones_${userId}     ({ [col]: string[] })
 *   3. DualSync→ ds_tombstones_${userId}_${collection} (array de strings)
 */
const _readTombstonesFromLS = (userId, collectionName) => {
  const ids = new Set();
  const tryParse = (key, extract) => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) extract(JSON.parse(raw));
    } catch (_) {}
  };
  tryParse(`tombstones_${userId}_${collectionName}`, (arr) => {
    if (Array.isArray(arr)) arr.forEach((id) => ids.add(String(id)));
  });
  tryParse(`imoveispro_tombstones_${userId}`, (obj) => {
    const arr = obj[collectionName];
    if (Array.isArray(arr)) arr.forEach((id) => ids.add(String(id)));
  });
  tryParse(`ds_tombstones_${userId}_${collectionName}`, (arr) => {
    if (Array.isArray(arr)) arr.forEach((id) => ids.add(String(id)));
  });
    // Formato 4: SyncManager V13 (imoveispro_tombstones_v13)
  tryParse('imoveispro_tombstones_v13', (obj) => {
    Object.entries(obj).forEach(([id, meta]) => {
      if (!meta.collection || meta.collection === collectionName) {
        ids.add(String(id));
      }
    });
  });
  return ids;
};

// ============================================================
// 🏗️  DatabaseService
// ============================================================

class DatabaseService {
  constructor(userId, companyId = null) {
    if (!userId || userId === "anonymous") {
      throw new Error("❌ DatabaseService: userId inválido ou 'anonymous'");
    }
    this.userId    = userId;
    this.companyId = companyId;
    this.dbKey     = this._getDatabaseKey();

    // IndexedDB
    this._indexedDB  = null;
    this._idbPromise = null;

    // Cache empresa em memória
    this._company = null;

    // ── 🛡️  TOMBSTONE CACHE ─────────────────────────────────
    // { collectionName: { ids: Set<string>, expiresAt: number } }
    this._tombstoneCache  = {};
    this._TOMBSTONE_TTL   = 30_000; // 30 s
    // ─────────────────────────────────────────────────────────

    const migrated = migrateUserDataToIsolatedDb(userId);

    this.initializeData();
    this.migrateImages();
    this.migrateCalendar();
    this.migrateDocuments();
    this.migrateFinanceiro();
    this.migrateCompany();
    this.migratePropertiesEnhancements();
    this.migrateAssignUserId();
    this.migrateCalendarColors();
    this.migrateMessages();

    const db = this.getDatabase();
    const totalItems =
      (db.clients?.length        || 0) +
      (db.properties?.length     || 0) +
      (db.tasks?.length          || 0) +
      (db.messages?.length       || 0) +
      (db.documents?.length      || 0);

    if (totalItems > 100) {
      setTimeout(() => this.deduplicateAllCollections(), 2000);
    }

    console.log(
      `📦 Database inicializada para usuário: ${userId}, chave: ${this.dbKey}`,
      migrated ? "(com migração)" : "(nova)"
    );
  }

  // ==========================================================
  // 🛡️  TOMBSTONE — API INTERNA
  // ==========================================================

  /** Retorna Set de IDs deletados (cache TTL 30 s). */
  _getTombstones(collectionName) {
    const now    = Date.now();
    const cached = this._tombstoneCache[collectionName];
    if (cached && cached.expiresAt > now) return cached.ids;
    const ids = _readTombstonesFromLS(this.userId, collectionName);
    this._tombstoneCache[collectionName] = { ids, expiresAt: now + this._TOMBSTONE_TTL };
    return ids;
  }

  /** Invalida cache de uma coleção (chamar após delete local). */
  _invalidateTombstoneCache(collectionName) {
    delete this._tombstoneCache[collectionName];
  }

  /** Verifica O(1) se um ID está deletado. */
  _isDeleted(id, collectionName) {
    return this._getTombstones(collectionName).has(String(id));
  }

  /**
   * Grava tombstone no localStorage (mesmo formato do LocalStrategy V8+).
   * Chamado por TODOS os métodos delete*().
   */
  _writeTombstone(id, collectionName) {
    try {
      const key = `tombstones_${this.userId}_${collectionName}`;
      let arr = [];
      try { arr = JSON.parse(localStorage.getItem(key) || "[]"); } catch (_) {}
      if (!arr.includes(String(id))) {
        arr.push(String(id));
        localStorage.setItem(key, JSON.stringify(arr));
      }
            // 🆕 Compatibilidad con SyncManager V13 (formato que espera el PULL desde Firestore)
      try {
        const lsKey = 'imoveispro_tombstones_v13';
        const existing = JSON.parse(localStorage.getItem(lsKey) || '{}');
        existing[String(id)] = { deletedAt: Date.now(), collection: collectionName };
        localStorage.setItem(lsKey, JSON.stringify(existing));
      } catch(e) {}

      this._invalidateTombstoneCache(collectionName);
    } catch (e) {
      console.warn("⚠️ _writeTombstone falhou:", e);
    }
  }

  /**
   * Remove itens tombstoned + nulos de qualquer array.
   * Usado por todos os getters.
   */
  _filterTombstoned(items, collectionName) {
    if (!Array.isArray(items)) return [];
    const tombstones = this._getTombstones(collectionName);
    if (tombstones.size === 0) return items.filter(Boolean);
    return items.filter((item) => item && !tombstones.has(String(item.id)));
  }

  // ==========================================================
  // 🗝️  CHAVE E DB
  // ==========================================================

  _getDatabaseKey() {
    return `imoveispro_db_user_${this.userId}`;
  }

  _defaultCompany() {
    return {
      nome:"", logo:"", cnpj:"", endereco:"", telefone:"", email:"",
      cor:"#2563eb", slogan:"", mostrarMarcaDagua:true,
      cidade:"", uf:"", website:"",
      updatedAt: new Date().toISOString(),
      userId: this.userId,
    };
  }

  getDatabase() {
    const dbKey  = this._getDatabaseKey();
    const dbJson = localStorage.getItem(dbKey);

    if (!dbJson) {
      // Compatibilidade com DB compartilhada legada
      const sharedJson = localStorage.getItem("imoveispro_db");
      if (sharedJson) {
        try {
          const s = JSON.parse(sharedJson);
          const uid = this.userId;
          return {
            clients:       (s.clients        || []).filter(c => c.userId === uid),
            properties:    (s.properties     || []).filter(p => p.userId === uid),
            interactions:  (s.interactions   || []).filter(i => i.userId === uid),
            tasks:         (s.tasks          || []).filter(t => t.userId === uid),
            calendarEvents:(s.calendarEvents || []).filter(e => e.userId === uid),
            documents:     (s.documents      || []).filter(d => d.userId === uid),
            financeiro:    (s.financeiro     || []).filter(f => f.userId === uid),
            messages:      [],
            company:       s.company || this._defaultCompany(),
            userPreferences: {},
          };
        } catch (_) {}
      }
      return {
        clients:[], properties:[], interactions:[], tasks:[],
        calendarEvents:[], documents:[], financeiro:[], messages:[],
        company: this._defaultCompany(), userPreferences:{},
      };
    }

    try {
      return JSON.parse(dbJson);
    } catch (e) {
      console.error("❌ getDatabase: JSON corrompido, retornando estrutura vazia.", e);
      return {
        clients:[], properties:[], interactions:[], tasks:[],
        calendarEvents:[], documents:[], financeiro:[], messages:[],
        company: this._defaultCompany(), userPreferences:{},
      };
    }
  }

    saveDatabase(db) {
    // 🔥 V1: Dual write — localStorage + IndexedDB
    let localStorageSuccess = false;

    // 1. Guardar en localStorage (backup siempre disponible)
    try {
      const serialized = JSON.stringify(db);
      if (serialized.length > 4_500_000) {
        console.warn(
          `⚠️ saveDatabase: DB do usuário ${this.userId} está grande ` +
          `(${(serialized.length / 1_048_576).toFixed(1)} MB). Considere limpar dados antigos.`
        );
      }
      localStorage.setItem(this.dbKey, serialized);
      localStorageSuccess = true;
    } catch (e) {
      if (e.name === "QuotaExceededError" || e.name === "NS_ERROR_DOM_QUOTA_REACHED") {
        console.error("❌ saveDatabase: localStorage cheio!", e);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("storage:quota_exceeded", {
            detail: { userId: this.userId },
          }));
        }
      } else {
        console.error("❌ saveDatabase: erro inesperado.", e);
      }
      // No lanzar error todavía — IndexedDB puede salvarnos
    }

    // 2. Guardar en IndexedDB (asíncrono, no bloquea)
    if (typeof window !== 'undefined') {
      import('../lib/storage/indexedDB.js').then(({ saveUserData }) => {
        saveUserData(this.userId, db).then(() => {
          if (!localStorageSuccess) {
            console.log('✅ [database.js] DB guardada en IndexedDB (localStorage no disponible)');
          }
        }).catch((err) => {
          console.warn('⚠️ [database.js] Error guardando en IndexedDB:', err.message);
          if (!localStorageSuccess) {
            console.error('❌ [database.js] CRÍTICO: Ni localStorage ni IndexedDB disponibles');
            throw err;
          }
        });
      }).catch(() => {
        // IndexedDB module not available, localStorage is enough
        if (!localStorageSuccess) {
          throw new Error('QuotaExceededError: localStorage cheio e IndexedDB indisponível');
        }
      });
    } else if (!localStorageSuccess) {
      // Sin window (SSR) y localStorage falló
      throw new Error('QuotaExceededError: localStorage cheio (ambiente sem IndexedDB)');
    }
  }

    initializeData() {
    if (!localStorage.getItem(this.dbKey)) {
      const emptyDB = {
        clients:[], properties:[], interactions:[], tasks:[],
        calendarEvents:[], documents:[], financeiro:[], messages:[],
        company: this._defaultCompany(), userPreferences:{},
      };
      
      // Guardar en localStorage (respaldo)
      localStorage.setItem(this.dbKey, JSON.stringify(emptyDB));
      
      // También guardar en IndexedDB (asíncrono)
      if (typeof window !== 'undefined') {
        import('../lib/storage/indexedDB.js').then(({ saveUserData }) => {
          saveUserData(this.userId, emptyDB).catch(() => {});
        }).catch(() => {});
      }
      
      console.log(`✅ Base de dados criada para usuário ${this.userId}`);
    }
  }

  // ==========================================================
  // 👤  USUÁRIO DEMO
  // ==========================================================

  _isDemoUser() { return ["1","2","3"].includes(this.userId); }

  _shouldUseDemoData() {
    if (!this._isDemoUser()) return false;
    const db = this.getDatabase();
    return !(db.clients?.length > 0) && !(db.properties?.length > 0) && !(db.financeiro?.length > 0);
  }

  // ==========================================================
  // 📊  DADOS DEMO
  // ==========================================================

  _getDemoMonthlySales(months = 6) {
    const today  = new Date();
    const values = [450000,520000,480000,610000,590000,720000];
    return Array.from({ length: months }, (_, i) => {
      const date  = new Date(today.getFullYear(), today.getMonth() - (months - 1 - i), 1);
      const value = values[i] ?? 500000 + Math.random() * 200000;
      return {
        month: date.toLocaleDateString("pt-BR",{ month:"short", year:"numeric" }),
        value, fullDate: date,
        formattedValue: value.toLocaleString("pt-BR",{ style:"currency", currency:"BRL" }),
      };
    });
  }

  _getDemoMonthlyRentals(months = 6) {
    const today = new Date();
    const mv  = [12500,14800,16200,15900,17100,18500];
    const tv  = [150000,177600,194400,190800,205200,222000];
    const cnt = [8,10,11,10,12,13];
    return Array.from({ length: months }, (_, i) => {
      const date = new Date(today.getFullYear(), today.getMonth() - (months - 1 - i), 1);
      return {
        month: date.toLocaleDateString("pt-BR",{ month:"short", year:"numeric" }),
        monthlyValue: mv[i] ?? 15000,
        totalValue:   tv[i] ?? 180000,
        count:        cnt[i] ?? 10,
        fullDate: date, contracts:[],
      };
    });
  }

  _getDemoSalesGrowth() {
    return { percentage:18.5, trend:"up", formatted:"+18.5%" };
  }

  _getDemoDashboardStats() {
    return {
      activeProperties:24, activeClients:156, pendingTasks:8,
      monthlyRevenue:720000, conversionRate:68,
      recentClients:[
        { id:"demo1", name:"João Silva",     type:"Comprador", status:"Ativo"  },
        { id:"demo2", name:"Maria Santos",   type:"Vendedor",  status:"Ativo"  },
        { id:"demo3", name:"Pedro Oliveira", type:"Locatário", status:"Novo"   },
        { id:"demo4", name:"Ana Costa",      type:"Investidor",status:"Ativo"  },
        { id:"demo5", name:"Carlos Souza",   type:"Comprador", status:"Quente" },
      ],
      recentProperties:[
        { id:"demo1", title:"Apartamento 3 suítes - Moema",  price:850000,  status:"Disponível" },
        { id:"demo2", title:"Cobertura duplex - Itaim",      price:1250000, status:"Negociação" },
        { id:"demo3", title:"Casa com piscina - Alphaville", price:2100000, status:"Disponível" },
      ],
      recentInteractions:[],
    };
  }

  _getDemoMessagesStats() {
    return {
      total:342, today:18, thisWeek:87, thisMonth:342,
      byStatus:{ sent:180, delivered:95, read:62, failed:5 },
      uniqueClients:42,
      templates:{ welcome:45, followup:78, promotion:120, reminder:99 },
    };
  }

  // ==========================================================
  // 👤  PREFERÊNCIAS
  // ==========================================================

  getUserPreferences(userId = this.userId) {
    const db = this.getDatabase();
    if (!db.userPreferences) db.userPreferences = {};
    return db.userPreferences[userId] || {};
  }

  saveUserPreferences(data, userId = this.userId) {
    const db = this.getDatabase();
    if (!db.userPreferences) db.userPreferences = {};
    db.userPreferences[userId] = {
      ...(db.userPreferences[userId] || {}),
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveDatabase(db);
    return db.userPreferences[userId];
  }

  // ==========================================================
  // 🔄  MIGRAÇÕES
  // ==========================================================

  migrateImages() {
    const db = this.getDatabase();
    let changed = false;
    db.properties = (db.properties || []).map((p) => {
      if (!p.images) { changed = true; return { ...p, images:[] }; }
      return p;
    });
    if (changed) { this.saveDatabase(db); console.log("✅ Migração de imagens aplicada"); }
  }

  migrateAssignUserId() {
    const db = this.getDatabase();
    let changed = false;
    ["clients","properties","tasks","documents","calendarEvents","financeiro"].forEach((col) => {
      if (Array.isArray(db[col])) {
        db[col] = db[col].map((item) => {
          if (!item.userId) { changed = true; return { ...item, userId: this.userId }; }
          return item;
        });
      }
    });
    if (changed) { this.saveDatabase(db); console.log("✅ Migração: userId aplicado a todos os itens"); }
  }

  migrateFinanceiro() {
    const db = this.getDatabase();
    if (!Array.isArray(db.financeiro)) {
      db.financeiro = [];
      this.saveDatabase(db);
      console.log("✅ Migração do financeiro aplicada");
    }
  }

  migrateMessages() {
    const db = this.getDatabase();
    let changed = false;
    if (!Array.isArray(db.messages)) { db.messages = []; changed = true; }
    if (db.oldMessages?.length > 0) {
      const existingIds = new Set(db.messages.map((m) => m.id));
      const newMsgs = db.oldMessages.filter((m) => !existingIds.has(m.id));
      if (newMsgs.length > 0) {
        db.messages = [...db.messages, ...newMsgs];
        console.log(`📨 Migrados ${newMsgs.length} mensagens novas`);
      }
      delete db.oldMessages;
      changed = true;
    }
    const migKey = `imoveispro_migrated_messages_${this.userId}`;
    if (!localStorage.getItem(migKey)) localStorage.setItem(migKey, Date.now().toString());
    if (changed) this.saveDatabase(db);
  }

  migrateCompany() {
    const db = this.getDatabase();
    let changed = false;
    if (!db.company) {
      db.company = this._defaultCompany();
      changed = true;
    } else {
      const defaults = {
        slogan:"", mostrarMarcaDagua:true, cidade:"", uf:"", website:"",
        updatedAt: new Date().toISOString(), userId: this.userId,
      };
      Object.entries(defaults).forEach(([k, v]) => {
        if (db.company[k] === undefined) { db.company[k] = v; changed = true; }
      });
    }
    if (changed) { this.saveDatabase(db); console.log("✅ Migração de empresa aplicada (white label)"); }
  }

  migratePropertiesEnhancements() {
    const db = this.getDatabase();
    let changed = false;
    if (Array.isArray(db.properties)) {
      db.properties = db.properties.map((p) => {
        const u = { ...p };
        if (!u.status)                      { u.status = "Disponível";              changed = true; }
        if (u.featured === undefined)        { u.featured = false;                   changed = true; }
        if (!u.createdAt)                    { u.createdAt = new Date().toISOString();changed = true; }
        if (!Array.isArray(u.priceHistory))  { u.priceHistory = [];                  changed = true; }
        if (typeof u.views !== "number")     { u.views = 0;                          changed = true; }
        return u;
      });
    }
    if (changed) { this.saveDatabase(db); console.log("✅ Migração de melhorias de imóveis aplicada"); }
  }

  migrateDocuments() {
    const db = this.getDatabase();
    if (!Array.isArray(db.documents)) {
      db.documents = [];
      this.saveDatabase(db);
      console.log("✅ Migração de documentos aplicada");
    }
  }

  migrateCalendar() {
    const db = this.getDatabase();
    if (!Array.isArray(db.calendarEvents)) {
      db.calendarEvents = [];
      this.saveDatabase(db);
      console.log("✅ Migração do calendário aplicada");
    }
  }

  migrateCalendarColors() {
    const db = this.getDatabase();
    let changed = false;
    if (Array.isArray(db.calendarEvents)) {
      db.calendarEvents = db.calendarEvents.map((e) => {
        const u = { ...e };
        if (!u.color)  { u.color  = "#3b82f6";   changed = true; }
        if (!u.status) { u.status = "scheduled"; changed = true; }
        return u;
      });
    }
    if (changed) { this.saveDatabase(db); console.log("✅ Migração de cores do calendário aplicada"); }
  }

  // ==========================================================
  // 💰  CRUD FINANCEIRO
  // ==========================================================

  /** 🛡️ tombstone filter aplicado */
  getFinanceiro() {
    const db  = this.getDatabase();
    const raw = (db.financeiro || []).filter((f) => f && f.userId === this.userId);
    return this._filterTombstoned(raw, "financeiro");
  }

  getFinanceiroById(id) {
    if (!id || this._isDeleted(id, "financeiro")) return null;
    const db = this.getDatabase();
    return (db.financeiro || []).find((f) => f.id === id && f.userId === this.userId) || null;
  }

  createFinanceiro(data) {
    const db = this.getDatabase();
    if (!Array.isArray(db.financeiro)) db.financeiro = [];
    const newItem = {
      id: Date.now().toString(),
      userId: this.userId,
      title:      data.title    || "",
      type:       data.type,
      category:   data.category || "Geral",
      value:      Number(data.value),
      date:       data.date,
      clientId:   data.clientId   || null,
      propertyId: data.propertyId || null,
      createdAt:  new Date().toISOString(),
    };
    db.financeiro.push(newItem);
    this.saveDatabase(db);
    return newItem;
  }

  updateFinanceiro(id, updatedData) {
    const db  = this.getDatabase();
    const idx = (db.financeiro || []).findIndex((f) => f.id === id && f.userId === this.userId);
    if (idx === -1) return null;
    db.financeiro[idx] = { ...db.financeiro[idx], ...updatedData, updatedAt: new Date().toISOString() };
    this.saveDatabase(db);
    return db.financeiro[idx];
  }

  /** 🛡️ grava tombstone */
  deleteFinanceiro(id) {
    if (!id) return;
    this._writeTombstone(id, "financeiro");
    const db = this.getDatabase();
    db.financeiro = (db.financeiro || []).filter(
      (f) => !(f.id === id && f.userId === this.userId)
    );
    this.saveDatabase(db);
  }

  getFinanceiroByProperty(propertyId) {
    return this.getFinanceiro().filter((f) => f.propertyId === propertyId);
  }

  // ==========================================================
  // 🔥  FIRESTORE — FINANÇAS EM TEMPO REAL
  // ==========================================================

  _getFirestore() {
    try {
      const { db: firestoreInstance } = require("../lib/firebase/config");
      if (firestoreInstance && typeof firestoreInstance.collection === "function")
        return firestoreInstance;
    } catch (_) {
      console.log("📴 Firestore não disponível, usando localStorage");
    }
    return null;
  }

  onFinanceiroSnapshot(callback, onError) {
    const firestore = this._getFirestore();
    if (!firestore || !this.companyId) { callback(this.getFinanceiro()); return () => {}; }
    const q = query(
      collection(firestore, "companies", this.companyId, "financeiro"),
      where("userId","==",this.userId), orderBy("createdAt","desc")
    );
    return onSnapshot(
      q,
      (snapshot) => callback(snapshot.docs.map((d) => ({ id:d.id, ...d.data() }))),
      (error) => {
        console.warn("⚠️ Firestore offline:", error.message);
        if (onError) onError(error); else callback(this.getFinanceiro());
      }
    );
  }

  async createFinanceiroFirestore(data) {
    const firestore = this._getFirestore();
    if (!firestore || !this.companyId) return null;
    const docRef = await addDoc(
      collection(firestore,"companies",this.companyId,"financeiro"),
      { ...data, userId:this.userId, companyId:this.companyId,
        createdAt:new Date().toISOString(), updatedAt:new Date().toISOString() }
    );
    return docRef.id;
  }

  async deleteFinanceiroFirestore(docId) {
    const firestore = this._getFirestore();
    if (!firestore || !this.companyId) return false;
    await deleteDoc(doc(firestore,"companies",this.companyId,"financeiro",docId));
    return true;
  }

  // ==========================================================
  // 🏷️  VENDAS AUTOMÁTICAS
  // ==========================================================

  registerSale(propertyId, clientId = null) {
    try {
      const db = this.getDatabase();
      const property = db.properties.find((p) => p.id === propertyId && p.userId === this.userId);
      if (!property) { console.error("❌ Imóvel não encontrado:", propertyId); return null; }

      const client = clientId
        ? db.clients.find((c) => c.id === clientId && c.userId === this.userId)
        : null;

      const existingSale = (db.financeiro||[]).find(
        (f) => f.propertyId === propertyId && f.type === "receita" && f.category === "Venda de Imóvel"
      );
      if (existingSale) { console.log("⚠️ Venda já registrada para este imóvel"); return existingSale; }

      if (!Array.isArray(db.financeiro)) db.financeiro = [];

      const saleRecord = {
        id: `sale_${Date.now()}_${Math.random().toString(36).substr(2,5)}`,
        userId: this.userId,
        propertyId: property.id,
        clientId:   client?.id || null,
        title:      `Venda: ${property.title || "Imóvel"}`,
        type:       "receita",
        category:   "Venda de Imóvel",
        value:      Number(property.price) || 0,
        date:       new Date().toISOString().split("T")[0],
        createdAt:  new Date().toISOString(),
        status:     "concluido",
        description:`Venda do imóvel ${property.title||"sem título"}${client?` para ${client.name}`:""}`,
        metadata: {
          propertyStatus:"Vendido", previousStatus:property.status,
          clientName:client?.name||null, clientEmail:client?.email||null,
          autoGenerated:true, propertyTitle:property.title, propertyPrice:property.price,
        },
      };
      db.financeiro.push(saleRecord);
      this.saveDatabase(db);
      console.log(`✅ Venda registrada: ${saleRecord.title} — R$ ${saleRecord.value}`);
      return saleRecord;
    } catch (error) {
      console.error("❌ Erro ao registrar venda:", error);
      return null;
    }
  }

  updatePropertyStatus(propertyId, newStatus, clientId = null, additionalData = {}) {
    try {
      const db = this.getDatabase();
      const propertyIndex = db.properties.findIndex(
        (p) => p.id === propertyId && p.userId === this.userId
      );
      if (propertyIndex === -1) { console.error("❌ Imóvel não encontrado:", propertyId); return false; }

      const property  = db.properties[propertyIndex];
      const oldStatus = property.status;
      const isSelling = newStatus === "Vendido" && oldStatus !== "Vendido";
      const isRenting = newStatus === "Alugado" && oldStatus !== "Alugado";

      let finalClientId = clientId || ((isSelling||isRenting) ? property.clientId : null);

      db.properties[propertyIndex].status    = newStatus;
      db.properties[propertyIndex].updatedAt = new Date().toISOString();

      if (isSelling) {
        const sale = this.registerSale(propertyId, finalClientId);
        if (sale) {
          if (!Array.isArray(db.properties[propertyIndex].priceHistory))
            db.properties[propertyIndex].priceHistory = [];
          db.properties[propertyIndex].priceHistory.push({
            date:new Date().toISOString(), price:property.price, status:"Vendido", saleId:sale.id,
          });
          if (finalClientId)
            this.addNoteToClient(finalClientId, `🏠 Imóvel vendido: ${property.title} — R$ ${property.price}`);
        }
      }

      if (isRenting) {
        const rentalData = {
          monthlyValue:   additionalData.monthlyValue   || property.price,
          contractMonths: additionalData.contractMonths || 12,
          startDate:      additionalData.startDate,
          paymentDay:     additionalData.paymentDay     || 5,
          autoRenew:      additionalData.autoRenew      || false,
        };
        const rental = this.registerRental(propertyId, finalClientId, rentalData);
        if (rental) {
          if (!Array.isArray(db.properties[propertyIndex].rentalHistory))
            db.properties[propertyIndex].rentalHistory = [];
          db.properties[propertyIndex].rentalHistory.push({
            date:new Date().toISOString(),
            monthlyValue:rental.value, totalValue:rental.totalValue,
            clientId:finalClientId, contractMonths:rentalData.contractMonths,
            startDate:rental.contract.startDate, endDate:rental.contract.endDate,
            rentalId:rental.id, status:"active",
          });
          if (finalClientId)
            this.addNoteToClient(finalClientId, `📋 Imóvel alugado: ${property.title} — R$ ${rental.value}/mês (${rentalData.contractMonths} meses)`);
        }
      }

      this.saveDatabase(db);

      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("propertyStatusChanged", {
          detail:{ propertyId, oldStatus, newStatus, clientId:finalClientId, isSelling, isRenting },
        }));
      }
      return true;
    } catch (error) {
      console.error("❌ Erro ao atualizar status:", error);
      return false;
    }
  }

  getSalesByProperty(propertyId) {
    return this.getFinanceiro()
      .filter((f) => f.propertyId === propertyId && f.type === "receita" && f.category === "Venda de Imóvel")
      .sort((a,b) => new Date(b.date) - new Date(a.date));
  }

  // ==========================================================
  // 📈  GRÁFICOS E METAS
  // ==========================================================

  getMonthlySales(months = 6) {
    if (this._shouldUseDemoData()) return this._getDemoMonthlySales(months);
    try {
      const financeiro = this.getFinanceiro();
      const today = new Date();
      return Array.from({ length: months }, (_, i) => {
        const date = new Date(today.getFullYear(), today.getMonth() - (months-1-i), 1);
        const monthStr = date.toLocaleDateString("pt-BR",{ month:"short", year:"numeric" });
        const total = financeiro
          .filter((f) => {
            if (f.type !== "receita" || f.category !== "Venda de Imóvel") return false;
            const d = new Date(f.date || f.createdAt);
            return d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
          })
          .reduce((s,f) => s + Number(f.value || 0), 0);
        return {
          month: monthStr, value: total, fullDate: date,
          formattedValue: total.toLocaleString("pt-BR",{ style:"currency", currency:"BRL" }),
        };
      });
    } catch (error) { console.error("❌ getMonthlySales:", error); return []; }
  }

  getSalesGrowth() {
    if (this._shouldUseDemoData()) return this._getDemoSalesGrowth();
    try {
      const monthly = this.getMonthlySales(2);
      if (monthly.length < 2) return { percentage:0, trend:"stable", formatted:"0%" };
      const [prev, curr] = [monthly[0].value, monthly[1].value];
      if (prev === 0) return { percentage:100, trend:"up", formatted:"+100%" };
      const growth = Math.round(((curr-prev)/prev) * 1000) / 10;
      return {
        percentage: growth,
        trend: growth > 0 ? "up" : growth < 0 ? "down" : "stable",
        formatted: `${growth>0?"+":""}${growth}%`,
      };
    } catch (error) { console.error("❌ getSalesGrowth:", error); return { percentage:0, trend:"stable", formatted:"0%" }; }
  }

  getMonthlyGoal() {
    try {
      const prefs = this.getUserPreferences();
      return Number(prefs.monthlyGoal || prefs.metaMensal || prefs.goal || 50000);
    } catch (_) { return 50000; }
  }

  setMonthlyGoal(goal) {
    try {
      const value = Number(goal);
      if (isNaN(value) || value < 0) { console.warn("⚠️ Valor de meta inválido:", goal); return false; }
      this.saveUserPreferences({ monthlyGoal: value });
      return true;
    } catch (_) { return false; }
  }

  // ==========================================================
  // 🏷️  ALUGUÉIS AUTOMÁTICOS
  // ==========================================================

  registerRental(propertyId, clientId = null, rentalData = {}) {
    try {
      const db = this.getDatabase();
      const property = db.properties.find((p) => p.id === propertyId && p.userId === this.userId);
      if (!property) { console.error("❌ Imóvel não encontrado:", propertyId); return null; }

      const client = clientId
        ? db.clients.find((c) => c.id === clientId && c.userId === this.userId)
        : null;

      const existing = (db.financeiro||[]).find(
        (f) => f.propertyId === propertyId && f.type === "receita" &&
               f.category === "Aluguel" && f.status === "active"
      );
      if (existing) return existing;

      if (!Array.isArray(db.financeiro)) db.financeiro = [];

      const monthlyValue   = Number(rentalData.monthlyValue   || property.price || 0);
      const contractMonths = Number(rentalData.contractMonths || 12);
      const totalValue     = monthlyValue * contractMonths;
      const startDate      = rentalData.startDate || new Date().toISOString().split("T")[0];
      const endDate        = rentalData.endDate || (() => {
        const d = new Date(startDate);
        d.setMonth(d.getMonth() + contractMonths);
        return d.toISOString().split("T")[0];
      })();

      const rentalRecord = {
        id: `rental_${Date.now()}_${Math.random().toString(36).substr(2,5)}`,
        userId: this.userId,
        propertyId: property.id,
        clientId:   client?.id || null,
        title:      `Aluguel: ${property.title || "Imóvel"}`,
        type:       "receita",
        category:   "Aluguel",
        value:      monthlyValue,
        totalValue,
        date:       startDate,
        createdAt:  new Date().toISOString(),
        status:     "active",
        contract: {
          startDate, endDate, months:contractMonths, monthlyValue, totalValue,
          paymentDay: rentalData.paymentDay || 5,
          autoRenew:  rentalData.autoRenew  || false,
        },
        description: `Aluguel do imóvel ${property.title||"sem título"}${client?` para ${client.name}`:""} — ${contractMonths} meses`,
        metadata: {
          propertyStatus:"Alugado", previousStatus:property.status,
          clientName:client?.name||null, clientEmail:client?.email||null,
          autoGenerated:true, propertyTitle:property.title,
          propertyPrice:property.price, contractMonths,
        },
      };

      db.financeiro.push(rentalRecord);

      const pIdx = db.properties.findIndex((p) => p.id === propertyId && p.userId === this.userId);
      if (pIdx !== -1) {
        if (!Array.isArray(db.properties[pIdx].rentalHistory)) db.properties[pIdx].rentalHistory = [];
        db.properties[pIdx].rentalHistory.push({
          date:new Date().toISOString(), monthlyValue, totalValue,
          clientId:client?.id||null, clientName:client?.name||null,
          contractMonths, startDate, endDate, rentalId:rentalRecord.id, status:"active",
        });
      }

      this.saveDatabase(db);
      console.log(`✅ Aluguel registrado: ${rentalRecord.title} — R$ ${monthlyValue}/mês`);
      return rentalRecord;
    } catch (error) { console.error("❌ Erro ao registrar aluguel:", error); return null; }
  }

  getRentalsByProperty(propertyId) {
    return this.getFinanceiro()
      .filter((f) => f.propertyId === propertyId && f.type === "receita" && f.category === "Aluguel")
      .sort((a,b) => new Date(b.date) - new Date(a.date));
  }

  getActiveRentals() {
    try {
      const today = new Date().toISOString().split("T")[0];
      return this.getFinanceiro()
        .filter((f) => f.type === "receita" && f.category === "Aluguel" &&
                       f.status === "active" && f.contract?.endDate >= today)
        .sort((a,b) => new Date(a.contract?.endDate) - new Date(b.contract?.endDate));
    } catch (_) { return []; }
  }

  renewRental(rentalId, renewalData = {}) {
    try {
      const db = this.getDatabase();
      const existing = (db.financeiro||[]).find(
        (f) => f.id === rentalId && f.userId === this.userId && f.category === "Aluguel"
      );
      if (!existing) { console.error("❌ Aluguel não encontrado:", rentalId); return null; }
      existing.status = "finished";
      existing.contract.autoRenew = false;
      const newRental = this.registerRental(existing.propertyId, existing.clientId, {
        monthlyValue:   renewalData.monthlyValue   || existing.value,
        contractMonths: renewalData.contractMonths || existing.contract.months,
        startDate:      renewalData.startDate      || new Date().toISOString().split("T")[0],
        paymentDay:     renewalData.paymentDay     || existing.contract.paymentDay,
        autoRenew:      renewalData.autoRenew      || false,
      });
      this.saveDatabase(db);
      return newRental;
    } catch (error) { console.error("❌ Erro ao renovar aluguel:", error); return null; }
  }

  cancelRental(rentalId) {
    try {
      const db  = this.getDatabase();
      const idx = (db.financeiro||[]).findIndex(
        (f) => f.id === rentalId && f.userId === this.userId && f.category === "Aluguel"
      );
      if (idx === -1) { console.error("❌ Aluguel não encontrado:", rentalId); return false; }
      db.financeiro[idx].status      = "cancelled";
      db.financeiro[idx].cancelledAt = new Date().toISOString();
      const prop = (db.properties||[]).find((p) => p.id === db.financeiro[idx].propertyId);
      if (prop?.rentalHistory) {
        const hi = prop.rentalHistory.findIndex((h) => h.rentalId === rentalId);
        if (hi !== -1) {
          prop.rentalHistory[hi].status      = "cancelled";
          prop.rentalHistory[hi].cancelledAt = new Date().toISOString();
        }
      }
      this.saveDatabase(db);
      return true;
    } catch (error) { console.error("❌ Erro ao cancelar aluguel:", error); return false; }
  }

  // ==========================================================
  // 📊  RELATÓRIOS
  // ==========================================================

  getMonthlyRentals(months = 6) {
    if (this._shouldUseDemoData()) return this._getDemoMonthlyRentals(months);
    try {
      const alugueis = this.getFinanceiro().filter((f) => f.category === "Aluguel");
      const today = new Date();
      return Array.from({ length: months }, (_, i) => {
        const date = new Date(today.getFullYear(), today.getMonth() - (months-1-i), 1);
        const monthStr = date.toLocaleDateString("pt-BR",{ month:"short", year:"numeric" });
        const monthRentals = alugueis.filter((a) => {
          const d = new Date(a.date || a.createdAt);
          return d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
        });
        const total    = monthRentals.reduce((s,a) => s + (Number(a.value)||0), 0);
        const totalC   = monthRentals.reduce((s,a) => s + (Number(a.totalValue)||0), 0);
        return {
          month:monthStr, monthlyValue:total, totalValue:totalC,
          count:monthRentals.length, fullDate:date,
          contracts:monthRentals.map((a) => ({
            id:a.id, title:a.title, monthlyValue:a.value, totalValue:a.totalValue,
            clientId:a.clientId, propertyId:a.propertyId, status:a.status,
          })),
        };
      });
    } catch (error) { console.error("❌ getMonthlyRentals:", error); return []; }
  }

  getRentalsReport(month, year) {
    try {
      const financeiro = this.getFinanceiro();
      const alugueis   = financeiro.filter((f) => {
        if (f.category !== "Aluguel") return false;
        const d = new Date(f.date || f.createdAt);
        return d.getMonth() === month-1 && d.getFullYear() === year;
      });
      const ativos       = financeiro.filter((f) => f.category === "Aluguel" && f.status === "active");
      const totalMensal  = alugueis.reduce((s,a) => s + (Number(a.value)||0), 0);
      const totalC       = alugueis.reduce((s,a) => s + (Number(a.totalValue)||0), 0);
      const porStatus    = {
        active:   alugueis.filter((a) => a.status === "active").length,
        finished: alugueis.filter((a) => a.status === "finished").length,
        cancelled:alugueis.filter((a) => a.status === "cancelled").length,
      };
      const db = this.getDatabase();
      const alugueisDetalhados = alugueis.map((a) => {
        const property = (db.properties||[]).find((p) => p.id === a.propertyId);
        const client   = (db.clients||[]).find((c) => c.id === a.clientId);
        return { ...a,
          propertyTitle: property?.title || "Imóvel não encontrado",
          clientName:    client?.name    || "Cliente não encontrado",
          clientPhone:   client?.phone   || null,
        };
      });
      return {
        period: { month, year },
        summary: {
          totalContracts:totalC, totalMonthlyValue:totalMensal,
          totalContractValue:totalC, activeContracts:ativos.length,
          averageMonthlyValue: alugueis.length ? totalMensal/alugueis.length : 0,
        },
        byStatus: porStatus,
        contracts: alugueisDetalhados,
        activeContractsList: ativos.map((a) => {
          const property = (db.properties||[]).find((p) => p.id === a.propertyId);
          const client   = (db.clients||[]).find((c) => c.id === a.clientId);
          return {
            id:a.id, propertyTitle:property?.title, clientName:client?.name,
            monthlyValue:a.value, endDate:a.contract?.endDate,
            monthsLeft: a.contract?.endDate
              ? Math.ceil((new Date(a.contract.endDate) - new Date()) / (1000*60*60*24*30)) : 0,
          };
        }),
      };
    } catch (error) { console.error("❌ getRentalsReport:", error); return null; }
  }

  getFinancialSummary(month, year) {
    try {
      const financeiro        = this.getFinanceiro();
      const registrosPeriodo  = financeiro.filter((f) => {
        const d = new Date(f.date || f.createdAt);
        return d.getMonth() === month-1 && d.getFullYear() === year;
      });
      const receitas    = registrosPeriodo.filter((f) => f.type === "receita");
      const despesas    = registrosPeriodo.filter((f) => f.type === "despesa");
      const vendas      = receitas.filter((f) => f.category === "Venda de Imóvel");
      const alugueis    = receitas.filter((f) => f.category === "Aluguel");
      const outrasR     = receitas.filter((f) => f.category !== "Venda de Imóvel" && f.category !== "Aluguel");
      const totalR      = receitas.reduce((s,r) => s + (Number(r.value)||0), 0);
      const totalD      = despesas.reduce((s,d) => s + (Number(d.value)||0), 0);
      const totalV      = vendas.reduce((s,v) => s + (Number(v.value)||0), 0);
      const totalA      = alugueis.reduce((s,a) => s + (Number(a.value)||0), 0);
      return {
        period:{ month, year },
        summary:{
          totalRevenue:totalR, totalExpenses:totalD, netBalance:totalR-totalD,
          revenue:{ total:totalR, byCategory:{ sales:totalV, rentals:totalA,
            other:outrasR.reduce((s,o) => s + (Number(o.value)||0),0) } },
          expenses:{ total:totalD, byCategory:despesas.reduce((acc,d) => {
            acc[d.category] = (acc[d.category]||0) + (Number(d.value)||0); return acc; },{}) },
        },
        sales:{ total:totalV, count:vendas.length, average:vendas.length?totalV/vendas.length:0,
          list:vendas.map((v) => ({ id:v.id, title:v.title, value:v.value, date:v.date, propertyId:v.propertyId, clientId:v.clientId })) },
        rentals:{ total:totalA, count:alugueis.length, average:alugueis.length?totalA/alugueis.length:0,
          list:alugueis.map((a) => ({ id:a.id, title:a.title, value:a.value, totalValue:a.totalValue, date:a.date, propertyId:a.propertyId, clientId:a.clientId, status:a.status })) },
        vendasMes:{ total:totalV, quantidade:vendas.length, valorMedio:vendas.length?totalV/vendas.length:0 },
        charts:{
          revenueByCategory:[
            { name:"Vendas", value:totalV }, { name:"Aluguéis", value:totalA },
            { name:"Outros", value:totalR-totalV-totalA },
          ],
          expensesByCategory: Object.entries(
            despesas.reduce((acc,d) => { acc[d.category]=(acc[d.category]||0)+(Number(d.value)||0); return acc; },{})
          ).map(([name,value]) => ({ name, value })),
        },
      };
    } catch (error) { console.error("❌ getFinancialSummary:", error); return null; }
  }

  getSalesReport(month, year) {
    try {
      const financeiro = this.getFinanceiro();
      const db = this.getDatabase();
      const vendas = financeiro.filter((f) => {
        if (f.category !== "Venda de Imóvel") return false;
        const d = new Date(f.date || f.createdAt);
        return d.getMonth() === month-1 && d.getFullYear() === year;
      });
      const vendasDetalhadas = vendas.map((v) => {
        const property = (db.properties||[]).find((p) => p.id === v.propertyId);
        const client   = (db.clients||[]).find((c) => c.id === v.clientId);
        return { ...v,
          propertyTitle:   property?.title   || "Imóvel não encontrado",
          propertyAddress: property?.address || null,
          clientName:      client?.name      || "Cliente não encontrado",
          clientPhone:     client?.phone     || null,
          clientEmail:     client?.email     || null,
        };
      });
      const totalVendas = vendas.reduce((s,v) => s + (Number(v.value)||0), 0);
      return {
        period:{ month, year },
        summary:{ totalSales:vendas.length, totalValue:totalVendas, averageValue:vendas.length?totalVendas/vendas.length:0 },
        sales: vendasDetalhadas,
      };
    } catch (error) { console.error("❌ getSalesReport:", error); return null; }
  }

  // ==========================================================
  // 🏢  EMPRESA
  // ==========================================================

  saveCompany(companyData) {
    const db = this.getDatabase();
    if (!db.company) db.company = this._defaultCompany();
    db.company = { ...db.company, ...companyData, updatedAt:new Date().toISOString(), userId:this.userId };
    this.saveDatabase(db);
    this._company = db.company;
    return db.company;
  }

  getCompany() {
    if (this._company) return this._company;
    const db = this.getDatabase();
    if (!db.company) { db.company = this._defaultCompany(); this.saveDatabase(db); }
    this._company = db.company;
    return db.company;
  }

  // ==========================================================
  // 📊  DASHBOARD
  // ==========================================================

  getDashboardStats() {
    if (this._shouldUseDemoData()) return this._getDemoDashboardStats();
    const db = this.getDatabase();
    // 🛡️ aplica tombstone filter
    const userClients    = this._filterTombstoned(
      (db.clients    ||[]).filter((c) => c.userId === this.userId), "clients");
    const userProperties = this._filterTombstoned(
      (db.properties ||[]).filter((p) => p.userId === this.userId), "properties");
    const userTasks      = this._filterTombstoned(
      (db.tasks      ||[]).filter((t) => t.userId === this.userId), "tasks");
    return {
      activeProperties: userProperties.filter((p) => p.status === "Disponível").length,
      activeClients:    userClients.filter((c) => c.status === "Ativo").length,
      pendingTasks:     userTasks.filter((t) => t.status === "pending").length,
      monthlyRevenue:   4500,
      conversionRate:   65,
      recentClients:    userClients.slice(0,5),
      recentProperties: userProperties.slice(0,3),
      recentInteractions: [],
    };
  }

  // ==========================================================
  // 💬  CRUD MESSAGES
  // ==========================================================

  /** 🛡️ tombstone filter aplicado */
  getMessages() {
    const db  = this.getDatabase();
    const raw = (db.messages||[]).filter((m) => m && m.userId === this.userId);
    return this._filterTombstoned(raw, "messages")
      .sort((a,b) => (b.createdAt||0) - (a.createdAt||0));
  }

  getMessageById(id) {
    if (!id || this._isDeleted(id,"messages")) return null;
    const db = this.getDatabase();
    return (db.messages||[]).find((m) => m.id === id && m.userId === this.userId) || null;
  }

  createMessage(data) {
    const db = this.getDatabase();
    if (!data.clientId) console.warn("⚠️ Mensagem sem clientId");
    const newMessage = {
      id: Date.now().toString(),
      userId:    this.userId,
      clientId:  data.clientId  || null,
      propertyId:data.propertyId|| null,
      template:  data.template  || "personalizado",
      message:   data.message   || "",
      phone:     data.phone     || "",
      type:      data.type      || "whatsapp",
      status:    data.status    || "sent",
      direction: data.direction || "outgoing",
      opened:    data.opened    || false,
      responded: data.responded || false,
      createdAt: Date.now(),
      sentAt:    data.sentAt    || Date.now(),
      attachments: data.attachments || [],
      notes:     data.notes || "",
    };
    if (!Array.isArray(db.messages)) db.messages = [];
    db.messages.push(newMessage);
    this.saveDatabase(db);
    return newMessage;
  }

  updateMessage(id, updatedData) {
    const db  = this.getDatabase();
    const idx = (db.messages||[]).findIndex((m) => m.id === id && m.userId === this.userId);
    if (idx === -1) return null;
    db.messages[idx] = { ...db.messages[idx], ...updatedData, updatedAt:Date.now() };
    this.saveDatabase(db);
    return db.messages[idx];
  }

  /** 🛡️ grava tombstone */
  deleteMessage(id) {
    if (!id) return;
    this._writeTombstone(id, "messages");
    const db = this.getDatabase();
    if (Array.isArray(db.messages)) {
      db.messages = db.messages.filter((m) => !(m.id === id && m.userId === this.userId));
      this.saveDatabase(db);
    }
  }

  getMessagesByClient(clientId) {
    return this.getMessages()
      .filter((m) => m.clientId === clientId)
      .sort((a,b) => (b.createdAt||0) - (a.createdAt||0));
  }

  getLastMessageByClient(clientId) {
    const msgs = this.getMessagesByClient(clientId);
    return msgs.length > 0 ? msgs[0] : null;
  }

  getMessagesStats(period = "all") {
    if (this._shouldUseDemoData()) return this._getDemoMessagesStats();
    const messages = this.getMessages();
    const now = Date.now(), oneDay = 86_400_000;
    return {
      total:     messages.length,
      today:     messages.filter((m) => m.createdAt > now - oneDay).length,
      thisWeek:  messages.filter((m) => m.createdAt > now - 7*oneDay).length,
      thisMonth: messages.filter((m) => m.createdAt > now - 30*oneDay).length,
      byStatus: {
        sent:      messages.filter((m) => m.status === "sent").length,
        delivered: messages.filter((m) => m.status === "delivered").length,
        read:      messages.filter((m) => m.status === "read").length,
        failed:    messages.filter((m) => m.status === "failed").length,
      },
      uniqueClients: new Set(messages.map((m) => m.clientId).filter(Boolean)).size,
      templates: messages.reduce((acc,m) => { acc[m.template]=(acc[m.template]||0)+1; return acc; }, {}),
    };
  }

  getMessagesByDateRange(startDate, endDate) {
    return this.getMessages().filter((m) => m.createdAt >= startDate && m.createdAt <= endDate);
  }

  // ==========================================================
  // 👤  CRUD CLIENTES
  // ==========================================================

  /** 🛡️ tombstone filter aplicado */
  getClients() {
    const db  = this.getDatabase();
    const raw = (db.clients||[]).filter((c) => c && c.userId === this.userId);
    return this._filterTombstoned(raw, "clients");
  }

  getClientById(id) {
    if (!id || this._isDeleted(id,"clients")) return null;
    const db = this.getDatabase();
    return (db.clients||[]).find((c) => c.id === id && c.userId === this.userId) || null;
  }

  createClient(data) {
    const db = this.getDatabase();
    const newClient = {
      id: Date.now().toString(), userId:this.userId,
      createdAt:new Date().toISOString(), updatedAt:new Date().toISOString(),
      name:  data.name  || "", email: data.email || "", phone:    data.phone    || "",
      type:  data.type  || "Comprador",  status:   data.status   || "Novo",
      pipeline:    data.pipeline    || "Lead Novo",
      temperature: data.temperature || "Morno",
      source:      data.source      || "Outros",
      budget:         data.budget         || "",
      expectedValue:  data.expectedValue  || 0,
      closedValue:    data.closedValue    || 0,
      score:          data.score          || 0,
      tags:           data.tags           || [],
      notes:[], lastContactAt:"",
    };
    db.clients.push(newClient);
    this.saveDatabase(db);
    return newClient;
  }

  updateClient(id, updatedData) {
    const db  = this.getDatabase();
    const idx = (db.clients||[]).findIndex((c) => c.id === id && c.userId === this.userId);
    if (idx !== -1) {
      db.clients[idx] = { ...db.clients[idx], ...updatedData, updatedAt:new Date().toISOString() };
      this.saveDatabase(db);
    }
  }

  /** 🛡️ grava tombstone */
  deleteClient(id) {
    this._writeTombstone(id, "clients");
    const db = this.getDatabase();
    db.clients = (db.clients||[]).filter((c) => !(c.id === id && c.userId === this.userId));
    if (Array.isArray(db.tasks))
      db.tasks = db.tasks.filter((t) => t.clientId !== id || t.userId !== this.userId);
    if (Array.isArray(db.properties))
      db.properties = db.properties.map((p) =>
        p.clientId === id && p.userId === this.userId ? { ...p, clientId:null } : p
      );
    this.saveDatabase(db);
  }

  getTasksByClient(clientId) {
    const db = this.getDatabase();
    if (!Array.isArray(db.tasks)) return [];
    return this._filterTombstoned(
      (db.tasks||[]).filter((t) => t && t.clientId === clientId && t.userId === this.userId),
      "tasks"
    );
  }

  // ==========================================================
  // 📝  NOTAS DE CLIENTE
  // ==========================================================

  addNoteToClient(clientId, noteText) {
    const db = this.getDatabase();
    const client = (db.clients||[]).find((c) => c.id === clientId && c.userId === this.userId);
    if (!client) return;
    const note = {
      id:Date.now().toString(), text:noteText,
      createdAt:new Date().toISOString(), createdBy:this.userId,
      editedAt:null, editedBy:null,
    };
    if (!Array.isArray(client.notes)) client.notes = [];
    client.notes.unshift(note);
    client.updatedAt = new Date().toISOString();
    this.saveDatabase(db);
    return note;
  }

  deleteNoteFromClient(clientId, noteId) {
    const db = this.getDatabase();
    const client = (db.clients||[]).find((c) => c.id === clientId && c.userId === this.userId);
    if (!client || !Array.isArray(client.notes)) return;
    client.notes = client.notes.filter((n) => n.id !== noteId);
    client.updatedAt = new Date().toISOString();
    this.saveDatabase(db);
  }

  editNoteFromClient(clientId, noteId, newText) {
    const db = this.getDatabase();
    const client = (db.clients||[]).find((c) => c.id === clientId && c.userId === this.userId);
    if (!client || !Array.isArray(client.notes)) return null;
    const noteIdx = client.notes.findIndex((n) => n.id === noteId);
    if (noteIdx === -1) return null;
    client.notes[noteIdx] = {
      ...client.notes[noteIdx], text:newText,
      editedAt:new Date().toISOString(), editedBy:this.userId,
    };
    client.updatedAt = new Date().toISOString();
    this.saveDatabase(db);
    return client.notes[noteIdx];
  }

  // ==========================================================
  // 🏠  CRUD IMÓVEIS
  // ==========================================================

  /** 🛡️ tombstone filter aplicado */
  getProperties() {
    const db  = this.getDatabase();
    const raw = (db.properties||[]).filter((p) => p && p.userId === this.userId);
    return this._filterTombstoned(raw, "properties");
  }

  getPropertyById(id) {
    if (!id || this._isDeleted(id,"properties")) return null;
    const db = this.getDatabase();
    return (db.properties||[]).find((p) => p.id === id && p.userId === this.userId) || null;
  }

  createProperty(data) {
    const db = this.getDatabase();
    const newProperty = {
      id:Date.now().toString(), userId:this.userId,
      createdAt:new Date().toISOString(), updatedAt:new Date().toISOString(),
      title:       data.title       || "", description: data.description || "",
      price:       data.price       || "", city:        data.city        || "",
      address:     data.address     || "", neighborhood:data.neighborhood|| "",
      type:        data.type        || "", bedrooms:    data.bedrooms    || "",
      bathrooms:   data.bathrooms   || "", area:        data.area        || "",
      garage:      data.garage      || "", iptu:        data.iptu        || "",
      condoFee:    data.condoFee    || "",
      code:        data.code || data.id || "",
      status:      data.status  || "Disponível",
      featured:    data.featured|| false,
      images:      data.images  || [],
      clientId:    data.clientId|| null,
      views:0, priceHistory:[], published:true,
    };
    db.properties.push(newProperty);
    this.saveDatabase(db);
    return newProperty;
  }

  updateProperty(id, updatedData) {
    const db  = this.getDatabase();
    const idx = (db.properties||[]).findIndex((p) => p.id === id && p.userId === this.userId);
    if (idx !== -1) {
      db.properties[idx] = {
        ...db.properties[idx], ...updatedData,
        images:    updatedData.images || db.properties[idx].images || [],
        updatedAt: new Date().toISOString(),
      };
      this.saveDatabase(db);
    }
  }

    linkPropertyToClient(propertyId, clientId) {
    const db = this.getDatabase();
    const property = (db.properties || []).find((p) => p.id === propertyId && p.userId === this.userId);
    if (!property) return false;
    
    // 1. Vincular en IndexedDB
    property.clientId  = clientId;
    property.updatedAt = new Date().toISOString();
    this.saveDatabase(db);
    
    // 2. 🆕 Sincronizar con Firestore para que PULL no deshaga el vínculo
    try {
      const syncMgr = window._dataContextInstance?._syncManager || 
                      window._dataContextDebug?._syncManager;
      if (syncMgr?.update) {
        syncMgr.update('properties', propertyId, { 
          clientId: clientId, 
          updatedAt: property.updatedAt 
        }).catch(e => console.warn('⚠️ Sync link falló:', e.message));
      }
      if (syncMgr?.markRecentlyModified) {
        syncMgr.markRecentlyModified(propertyId);
      }
    } catch(e) {
      console.warn('⚠️ No se pudo sincronizar link con Firestore:', e.message);
    }
    
    return true;
  }

  unlinkProperty(propertyId) {
    const db = this.getDatabase();
    const property = (db.properties || []).find((p) => p.id === propertyId && p.userId === this.userId);
    if (!property) return false;
    
    const previousClientId = property.clientId;
    
    // 1. Desvincular en IndexedDB
    property.clientId  = null;
    property.updatedAt = new Date().toISOString();
    this.saveDatabase(db);
    
    // 2. Sincronizar con Firestore para que PULL no restaure el vínculo
    try {
      const syncMgr = window._dataContextInstance?._syncManager || 
                      window._dataContextDebug?._syncManager;
      if (syncMgr?.update) {
        syncMgr.update('properties', propertyId, { 
          clientId: null, 
          updatedAt: property.updatedAt 
        }).catch(e => console.warn('⚠️ Sync unlink falló:', e.message));
      }
      if (syncMgr?.markRecentlyModified) {
        syncMgr.markRecentlyModified(propertyId);
      }
    } catch(e) {
      console.warn('⚠️ No se pudo sincronizar unlink con Firestore:', e.message);
    }
    
    return true;
  }
  
  // ==========================================================
  // ✅  CRUD TAREFAS
  // ==========================================================

  /** 🛡️ tombstone filter aplicado */
  getTasks() {
    const db = this.getDatabase();
    if (!Array.isArray(db.tasks)) {
      db.tasks = [];
      this.saveDatabase(db);
    }
    const raw = db.tasks.filter((t) => {
      if (!t) return false;
      if (!t.userId) { t.userId = this.userId; this.saveDatabase(db); }
      return t.userId === this.userId;
    });
    return this._filterTombstoned(raw, "tasks");
  }

  getTaskById(id) {
    if (!id) { console.warn("⚠️ getTaskById: id não fornecido"); return null; }
    if (this._isDeleted(id,"tasks")) return null;
    const db = this.getDatabase();
    if (!Array.isArray(db.tasks)) return null;
    return db.tasks.find((t) => t && t.id === id && t.userId === this.userId) || null;
  }

  createTask(data) {
    if (!data || typeof data !== "object") throw new Error("Dados inválidos para criar tarefa");
    if (!data.title?.trim()) throw new Error("Título da tarefa é obrigatório");
    const db = this.getDatabase();
    if (!Array.isArray(db.tasks)) db.tasks = [];
    const newTask = {
      id:Date.now().toString(), userId:this.userId,
      title:       data.title.trim(),
      description: data.description || "",
      status:  ["pending","in_progress","done"].includes(data.status) ? data.status : "pending",
      priority:["low","medium","high"].includes(data.priority)        ? data.priority: "medium",
      dueDate:     data.dueDate  || null,
      createdAt:   new Date().toISOString(),
      clientId:    data.clientId  || null,
      propertyId:  data.propertyId|| null,
      assignedTo:  data.assignedTo|| this.userId,
      completedAt: null, completedBy: null,
      tags:        Array.isArray(data.tags)        ? data.tags        : [],
      attachments: Array.isArray(data.attachments) ? data.attachments : [],
    };
    db.tasks.push(newTask);
    this.saveDatabase(db);
    console.log(`✅ Tarefa criada: ${newTask.id} — ${newTask.title}`);
    return newTask;
  }

  updateTask(id, updatedData) {
    if (!id || !updatedData || typeof updatedData !== "object") return false;
    const db = this.getDatabase();
    if (!Array.isArray(db.tasks)) return false;
    const idx = db.tasks.findIndex((t) => t && t.id === id && t.userId === this.userId);
    if (idx === -1) { console.warn(`⚠️ updateTask: Tarefa não encontrada (id: ${id})`); return false; }
    const old     = { ...db.tasks[idx] };
    const updates = { ...updatedData };
    if (updatedData.status === "done"  && old.status !== "done") {
      updates.completedAt = new Date().toISOString(); updates.completedBy = this.userId;
    }
    if (old.status === "done" && updatedData.status && updatedData.status !== "done") {
      updates.completedAt = null; updates.completedBy = null;
    }
    db.tasks[idx] = { ...old, ...updates, updatedAt:new Date().toISOString(), lastModifiedBy:this.userId };
    this.saveDatabase(db);
    return true;
  }

  /** 🛡️ grava tombstone */
  deleteTask(id) {
    if (!id) { console.error("❌ deleteTask: id não fornecido"); return 0; }
    this._writeTombstone(id, "tasks");
    const db = this.getDatabase();
    if (!Array.isArray(db.tasks)) return 0;
    const before = db.tasks.length;
    db.tasks = db.tasks.filter((t) => t && !(t.id === id && t.userId === this.userId));
    const deleted = before - db.tasks.length;
    if (deleted > 0) this.saveDatabase(db);
    return deleted;
  }

  // ==========================================================
  // 📅  CRUD CALENDÁRIO
  // ==========================================================

  /** 🛡️ tombstone filter aplicado */
  getCalendarEvents() {
    const db  = this.getDatabase();
    const raw = (db.calendarEvents||[]).filter((e) => e && e.userId === this.userId);
    return this._filterTombstoned(raw, "calendarEvents");
  }

  getCalendarEventById(id) {
    if (!id || this._isDeleted(id,"calendarEvents")) return null;
    const db = this.getDatabase();
    return (db.calendarEvents||[]).find((e) => e.id === id && e.userId === this.userId) || null;
  }

  createCalendarEvent(data) {
    const db = this.getDatabase();
    const newEvent = {
      id:Date.now().toString(), userId:this.userId,
      title:       data.title,
      description: data.description || "",
      date:        data.date,
      timeStart:   data.timeStart  || null, timeEnd: data.timeEnd  || null,
      clientId:    data.clientId   || null, propertyId:data.propertyId||null,
      taskId:      data.taskId     || null,
      status:      data.status     || "scheduled",
      color:       data.color      || "#3b82f6",
      createdAt:   new Date().toISOString(),
    };
    db.calendarEvents.push(newEvent);
    this.saveDatabase(db);
    return newEvent;
  }

  updateCalendarEvent(id, updatedData) {
    const db  = this.getDatabase();
    const idx = (db.calendarEvents||[]).findIndex((e) => e.id === id && e.userId === this.userId);
    if (idx === -1) return false;
    db.calendarEvents[idx] = {
      ...db.calendarEvents[idx], ...updatedData,
      color:     updatedData.color || db.calendarEvents[idx].color,
      updatedAt: new Date().toISOString(),
    };
    this.saveDatabase(db);
    return true;
  }

  /** 🛡️ grava tombstone */
  deleteCalendarEvent(id) {
    if (!id) return false;
    this._writeTombstone(id, "calendarEvents");
    const db = this.getDatabase();
    const before = (db.calendarEvents||[]).length;
    db.calendarEvents = (db.calendarEvents||[]).filter(
      (e) => !(e.id === id && e.userId === this.userId)
    );
    if (db.calendarEvents.length < before) { this.saveDatabase(db); return true; }
    return false;
  }

  getCalendarEventsByClient(clientId) {
    return this.getCalendarEvents().filter((e) => e.clientId === clientId);
  }

  getCalendarEventsByProperty(propertyId) {
    return this.getCalendarEvents().filter((e) => e.propertyId === propertyId);
  }

  // ==========================================================
  // 📁  CRUD DOCUMENTOS
  // ==========================================================

  /** 🛡️ tombstone filter aplicado */
  getDocuments() {
    const db = this.getDatabase();
    if (!Array.isArray(db.documents)) { db.documents = []; this.saveDatabase(db); }
    const raw = db.documents.filter((d) => {
      if (!d) return false;
      if (!d.userId) { d.userId = this.userId; this.saveDatabase(db); }
      return d.userId === this.userId;
    });
    return this._filterTombstoned(raw, "documents")
      .sort((a,b) => {
        const dA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dB - dA;
      })
      .map((d) => {
        const copy = { ...d };
        if (copy.storage === "indexeddb") { delete copy.base64; copy.hasLargeFile = true; }
        else if (copy.storage === "local" && !copy.base64) { copy.hasLargeFile = true; }
        return copy;
      });
  }

  async getDocumentById(id) {
    if (!id) return null;
    if (this._isDeleted(id,"documents")) return null;
    const db = this.getDatabase();
    if (!Array.isArray(db.documents)) return null;
    const metadata = db.documents.find((d) => d && d.id === id && d.userId === this.userId);
    if (!metadata) return null;
    if (metadata.storage === "local" || !metadata.storage) return metadata;
    try {
      await this.initIndexedDB();
      const tx    = this._indexedDB.transaction(["documents"],"readonly");
      const store = tx.objectStore("documents");
      return new Promise((resolve) => {
        const req = store.get(id);
        req.onsuccess = () =>
          resolve(req.result ? { ...metadata, base64:req.result.base64 } : metadata);
        req.onerror = () => resolve(metadata);
      });
    } catch (_) { return metadata; }
  }

  createDocument(data) {
    if (!data || typeof data !== "object") throw new Error("Dados inválidos para criar documento");
    if (!data.title?.trim())               throw new Error("Título do documento é obrigatório");
    if (!data.base64)                      throw new Error("Arquivo do documento é obrigatório");

    const db = this.getDatabase();
    if (!Array.isArray(db.documents)) db.documents = [];

    const documentId = Date.now().toString();
    const IS_LARGE   = data.base64.length > 500 * 1024;

    const metadata = {
      id:documentId, userId:this.userId,
      title:       data.title.trim(),
      fileName:    data.fileName  || "documento.bin",
      fileType:    data.fileType  || "application/octet-stream",
      size:        data.base64.length,
      base64:      data.base64,
      clientId:    data.clientId   || null,
      propertyId:  data.propertyId || null,
      createdAt:   new Date().toISOString(),
      updatedAt:   new Date().toISOString(),
      createdBy:   this.userId,
      description: data.description || "",
      tags:        Array.isArray(data.tags) ? data.tags : [],
      isFavorite:  false, version:1, downloadCount:0,
      storage: IS_LARGE ? "indexeddb" : "local",
    };

    const saveDocument = async () => {
      try {
        if (IS_LARGE) {
          await this.initIndexedDB();
          const tx    = this._indexedDB.transaction(["documents"],"readwrite");
          const store = tx.objectStore("documents");
          await store.put({ id:documentId, base64:data.base64, userId:this.userId });
        }
        db.documents.push(metadata);
        this.saveDatabase(db);
      } catch (error) { console.error("❌ Erro ao salvar documento:", error); throw error; }
    };
    saveDocument().catch(console.error);
    return metadata;
  }

  /** 🛡️ grava tombstone */
  async deleteDocument(id) {
    if (!id) return false;
    this._writeTombstone(id, "documents");
    const db = this.getDatabase();
    if (!Array.isArray(db.documents)) return false;

    const docToDelete = db.documents.find((d) => d && d.id === id && d.userId === this.userId);
    const wasLarge    = docToDelete?.storage === "indexeddb";
    const before      = db.documents.length;

    db.documents = db.documents.filter((d) => d && !(d.id === id && d.userId === this.userId));

    if (db.documents.length < before) {
      this.saveDatabase(db);
      if (wasLarge) {
        try {
          await this.initIndexedDB();
          const tx = this._indexedDB.transaction(["documents"],"readwrite");
          await tx.objectStore("documents").delete(id);
        } catch (e) { console.error("❌ Erro ao remover do IndexedDB:", e); }
      }
      return true;
    }
    return false;
  }

  // ==========================================================
  // 🖼️  INDEXEDDB — IMAGENS + DOCUMENTOS
  // ==========================================================

  async initIndexedDB() {
    if (this._indexedDB)  return this._indexedDB;
    if (this._idbPromise) return this._idbPromise;
    this._idbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(`imoveispro_storage_${this.userId}`, 2);
      request.onerror      = () => reject(request.error);
      request.onsuccess    = () => { this._indexedDB = request.result; resolve(this._indexedDB); };
      request.onupgradeneeded = (event) => {
        const idb = event.target.result;
        if (!idb.objectStoreNames.contains("images")) {
          const s = idb.createObjectStore("images",{ keyPath:"id" });
          s.createIndex("propertyId","propertyId",{ unique:false });
          s.createIndex("clientId",  "clientId",  { unique:false });
          s.createIndex("createdAt", "createdAt", { unique:false });
        }
        if (!idb.objectStoreNames.contains("documents")) {
          const s = idb.createObjectStore("documents",{ keyPath:"id" });
          s.createIndex("propertyId","propertyId",{ unique:false });
          s.createIndex("clientId",  "clientId",  { unique:false });
          s.createIndex("createdAt", "createdAt", { unique:false });
        }
      };
    });
    return this._idbPromise;
  }

  async getImageFromIndexedDB(imageId) {
    if (!imageId) return null;
    try {
      await this.initIndexedDB();
      const tx    = this._indexedDB.transaction(["images"],"readonly");
      const store = tx.objectStore("images");
      return new Promise((resolve) => {
        const req = store.get(imageId);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror   = () => resolve(null);
      });
    } catch (_) { return null; }
  }

  async getPropertyPreviewImage(propertyId) {
    if (!propertyId) return null;
    try {
      await this.initIndexedDB();
      const tx    = this._indexedDB.transaction(["images"],"readonly");
      const store = tx.objectStore("images");
      const index = store.index("propertyId");
      return new Promise((resolve) => {
        const req = index.getAll(propertyId);
        req.onsuccess = () => {
          const images = req.result || [];
          if (!images.length) { resolve(null); return; }
          images.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
          resolve(images[0].data);
        };
        req.onerror = () => resolve(null);
      });
    } catch (_) { return null; }
  }

  async getImageById(imageId) {
    await this.initIndexedDB();
    return this.getImageFromIndexedDB(imageId);
  }

  async getPropertiesWithPreviews(propertyIds = null, options = {}) {
    const { limit = 50, loadPreviews = true } = options;
    const props = propertyIds
      ? propertyIds.map((id) => this.getPropertyById(id)).filter(Boolean)
      : this.getProperties();

    if (!loadPreviews) {
      return props.map((p) => ({ ...p, previewImage:null,
        hasImages:p.images?.length>0, imageCount:p.images?.length||0 }));
    }

    const toProcess = props.slice(0, limit);
    const enhanced  = await Promise.all(toProcess.map(async (prop) => {
      if (prop.images?.length > 0) {
        try {
          const previewImage = await this.getPropertyPreviewImage(prop.id);
          return { ...prop, previewImage:previewImage||null, hasImages:true, imageCount:prop.images.length };
        } catch (_) {
          return { ...prop, previewImage:null, hasImages:true, imageCount:prop.images.length };
        }
      }
      return { ...prop, previewImage:null, hasImages:false, imageCount:0 };
    }));

    const remaining = props.slice(limit).map((p) => ({
      ...p, previewImage:null, hasImages:p.images?.length>0, imageCount:p.images?.length||0,
    }));
    return [...enhanced, ...remaining];
  }

  async checkStorageSpace() {
    try {
      if (!navigator.storage?.estimate) return { supported:false };
      const estimate    = await navigator.storage.estimate();
      const percentUsed = ((estimate.usage / estimate.quota) * 100).toFixed(1);
      return {
        supported:true,
        usage:estimate.usage, quota:estimate.quota,
        usageMB:  (estimate.usage  / 1_048_576).toFixed(1),
        quotaMB:  (estimate.quota  / 1_048_576).toFixed(1),
        percentUsed,
        isLow:     percentUsed > 80,
        isCritical:percentUsed > 95,
      };
    } catch (error) { return { supported:false, error:error.message }; }
  }

  async saveImage(imageData, propertyId = null, clientId = null) {
    try {
      await this.initIndexedDB();
      if (navigator.storage?.estimate) {
        const est = await navigator.storage.estimate();
        const pct = (est.usage / est.quota) * 100;
        if (pct > 90)
          window?.dispatchEvent(new CustomEvent("storage:warning",{ detail:{ usage:pct } }));
      }
      return new Promise((resolve, reject) => {
        const tx    = this._indexedDB.transaction(["images"],"readwrite");
        const store = tx.objectStore("images");
        const id    = `img_${Date.now()}_${Math.random().toString(36).substr(2,9)}`;
        const req   = store.add({
          id, propertyId, clientId, data:imageData,
          userId:this.userId, createdAt:new Date().toISOString(), size:imageData.length,
        });
        req.onsuccess = () => resolve(id);
        req.onerror   = (e) => {
          const err = e.target.error;
          if (err.name === "QuotaExceededError" || err.name === "QuotaExceeded")
            reject(new Error("Espaço insuficiente no dispositivo. Libere espaço e tente novamente."));
          else reject(err);
        };
      });
    } catch (error) { console.error("❌ saveImage:", error); throw error; }
  }

  async saveMultipleImages(imagesArray, propertyId = null, clientId = null) {
    await this.initIndexedDB();
    const isMobile  = typeof window !== "undefined" && window.innerWidth < 768;
    const batchSize = isMobile ? 2 : 3;
    const savedIds  = [], errors = [];
    for (let i = 0; i < imagesArray.length; i += batchSize) {
      const batch   = imagesArray.slice(i, i + batchSize);
      const results = await Promise.all(batch.map(async (img) => {
        try { return { success:true, id:await this.saveImage(img, propertyId, clientId) }; }
        catch (e) { return { success:false, error:e.message }; }
      }));
      results.forEach((r) => { if (r.success) savedIds.push(r.id); else errors.push(r.error); });
      if (i + batchSize < imagesArray.length)
        await new Promise((res) => setTimeout(res, isMobile ? 50 : 10));
    }
    if (errors.length) console.warn(`⚠️ ${errors.length} imagens falharam ao salvar`);
    return savedIds;
  }

  async deleteMultipleImages(imageIds) {
    await this.initIndexedDB();
    const tx    = this._indexedDB.transaction(["images"],"readwrite");
    const store = tx.objectStore("images");
    return new Promise((resolve, reject) => {
      if (!imageIds.length) { resolve(); return; }
      let completed = 0, hasError = false;
      imageIds.forEach((id) => {
        const req = store.delete(id);
        req.onsuccess = () => { completed++; if (completed === imageIds.length && !hasError) resolve(); };
        req.onerror   = () => { if (!hasError) { hasError = true; reject(req.error); } };
      });
    });
  }

  async getImageStorageStats() {
    await this.initIndexedDB();
    return new Promise((resolve, reject) => {
      const tx  = this._indexedDB.transaction(["images"],"readonly");
      const req = tx.objectStore("images").getAll();
      req.onsuccess = () => {
        const images    = req.result || [];
        const totalSize = images.reduce((s,i) => s + (i.size||0), 0);
        resolve({
          count:images.length, totalSize,
          totalSizeMB:(totalSize/1_048_576).toFixed(2),
          byProperty: images.reduce((acc,img) => {
            if (img.propertyId) acc[img.propertyId] = (acc[img.propertyId]||0)+1;
            return acc;
          }, {}),
        });
      };
      req.onerror = () => reject(req.error);
    });
  }

  // ==========================================================
  // 🧹  DEDUPLICAÇÃO
  // ==========================================================

  deduplicateAllCollections() {
    const db = this.getDatabase();
    let changed = false;
    ["messages","clients","properties","tasks","documents","calendarEvents","financeiro"].forEach((col) => {
      if (!Array.isArray(db[col]) || !db[col].length) return;
      const map = new Map();
      db[col].forEach((item) => {
        if (!item?.id) return;
        const existing = map.get(item.id);
        if (!existing) { map.set(item.id, item); return; }
        const iDate = item.createdAt    ? new Date(item.createdAt)    : new Date(0);
        const eDate = existing.createdAt? new Date(existing.createdAt): new Date(0);
        if (iDate > eDate) map.set(item.id, item);
      });
      const unique = Array.from(map.values());
      if (unique.length !== db[col].length) {
        console.log(`🧹 Deduplicados ${col}: ${db[col].length - unique.length} removidos`);
        db[col] = unique; changed = true;
      }
    });
    if (changed) { this.saveDatabase(db); console.log("✅ Deduplicação concluída"); }
  }
}

// ============================================================
// 🗄️  SISTEMA GLOBAL DE USUÁRIOS
// ============================================================

export const getUsersDatabase = () => {
  const dbJson = localStorage.getItem("imoveispro_users_db");
  if (!dbJson) {
    const sharedJson = localStorage.getItem("imoveispro_db");
    if (sharedJson) {
      try {
        const shared = JSON.parse(sharedJson);
        if (shared.users?.length > 0) return { users: shared.users };
      } catch (_) {}
    }
    return {
      users: [
        { id:"1", email:"admin@imoveispro.com",    password:"123456", name:"Administrador",
          role:"admin",    avatar:null, company:"Imóveis Pro LTDA", phone:"+55 11 99999-9999", createdAt:"2024-01-15T10:30:00Z" },
        { id:"2", email:"corretor@imoveispro.com", password:"123456", name:"Carlos Silva",
          role:"corretor", avatar:null, company:"Imóveis Pro LTDA", phone:"+55 11 98888-8888", createdAt:"2024-01-20T14:15:00Z" },
        { id:"3", email:"ana@imoveispro.com",      password:"123456", name:"Ana Costa",
          role:"usuario",  avatar:null, company:"Costa Imóveis",    phone:"+55 11 97777-7777", createdAt:"2024-02-01T09:00:00Z" },
      ],
    };
  }
  try { return JSON.parse(dbJson); } catch (_) { return { users:[] }; }
};

export const saveUsersDatabase = (db) => {
  localStorage.setItem("imoveispro_users_db", JSON.stringify(db));
};

export const authenticateUser = async (email, password) => {
  try {
    const usersDb = getUsersDatabase();
    const user    = usersDb.users.find((u) => u.email === email);
    if (!user) return null;
    let isValid = false;
    if (!user.password.startsWith("$2")) {
      isValid = user.password === password;
    } else {
      isValid = await bcrypt.compare(password, user.password);
    }
    if (!isValid) return null;
    return { id:user.id, name:user.name, email:user.email, role:user.role,
             company:user.company, avatar:user.avatar, phone:user.phone, createdAt:user.createdAt };
  } catch (error) { console.error("❌ Erro na autenticação:", error); return null; }
};

// ============================================================
// 📢  VITRINE PÚBLICA
// ============================================================

export const getPublicPropertiesDatabase = () => {
  try {
    const dbJson = localStorage.getItem("imoveispro_public_properties");
    return dbJson ? JSON.parse(dbJson) : { properties:[] };
  } catch (_) { return { properties:[] }; }
};

export const savePublicPropertiesDatabase = (db) => {
  try { localStorage.setItem("imoveispro_public_properties", JSON.stringify(db)); return true; }
  catch (_) { return false; }
};

export const publishProperty = async (property, company) => {
  try {
    if (!property?.id) return null;
    const publicDb     = getPublicPropertiesDatabase();
    const publicImages = (property.images||[]).slice(0,10).filter(
      (img) => typeof img === "string" && (img.startsWith("data:") || img.startsWith("http"))
    );
    const publicProperty = {
      id:property.id, ownerId:property.userId,
      title:       property.title       || "Imóvel",
      price:       property.price       || 0,
      description: property.description || "",
      bedrooms:    property.bedrooms    || 0,
      bathrooms:   property.bathrooms   || 0,
      area:        property.area        || 0,
      type:        property.type        || "Imóvel",
      address:     property.address     || "",
      neighborhood:property.neighborhood|| "",
      city:        property.city        || "",
      status:      property.status      || "Disponível",
      featured:    property.featured    || false,
      iptu:        property.iptu        || null,
      condoFee:    property.condoFee    || null,
      images:      publicImages,
      company: {
        nome:             company?.nome     || "Imobiliária",
        cor:              company?.cor      || "#2563eb",
        logo:             company?.logo     || null,
        companyId:        property.companyId || company?.companyId || "company_qJ6VFdI1cKRKJbnLOjOAVbU71Yk2",
        slogan:           company?.slogan   || "",
        telefone:         company?.telefoneWhatsApp || company?.telefone || "",
        telefoneWhatsApp: company?.telefoneWhatsApp || company?.telefone || "",
        website:          company?.website  || "",
      },
      publishedAt: new Date().toISOString(),
      views: 0,
    };
    const idx = publicDb.properties.findIndex((p) => p.id === property.id);
    if (idx >= 0) {
      publicProperty.publishedAt = publicDb.properties[idx].publishedAt;
      publicProperty.views       = publicDb.properties[idx].views || 0;
      publicDb.properties[idx]   = publicProperty;
    } else {
      publicDb.properties.push(publicProperty);
    }
    savePublicPropertiesDatabase(publicDb);
    try {
      const { getFirestore, doc, setDoc } = await import("firebase/firestore");
      const { app }                        = await import("../lib/firebase/config.js");
      const fb = getFirestore(app);
      if (fb) await setDoc(doc(fb,"public_properties",publicProperty.id), publicProperty,{ merge:true });
    } catch (_) { console.warn("⚠️ Firestore offline"); }
    return publicProperty;
  } catch (error) { console.error("❌ Erro ao publicar:", error); return null; }
};

export const getPublicPropertyById = async (id) => {
  try {
    if (!id) return null;
    try {
      const { getFirestore, doc, getDoc } = await import("firebase/firestore");
      const { app }                        = await import("../lib/firebase/config.js");
      const fb = getFirestore(app);
      if (fb) {
        const snap = await getDoc(doc(fb,"public_properties",id));
        if (snap.exists()) return snap.data();
      }
    } catch (_) { console.warn("⚠️ Firestore offline, buscando local"); }
    const publicDb = getPublicPropertiesDatabase();
    let property = publicDb.properties.find((p) => p.id === id);
    if (property) { property.views = (property.views||0)+1; return property; }
    const keys = Object.keys(localStorage).filter((k) => k.startsWith("imoveispro_db_user_"));
    for (const key of keys) {
      try {
        const userDb = JSON.parse(localStorage.getItem(key)||"{}");
        property = (userDb.properties||[]).find((p) => p.id === id || p._cloudId === id);
        if (property) return property;
      } catch (_) {}
    }
    return null;
  } catch (error) { console.error("❌ Erro ao buscar:", error); return null; }
};

export const getRecommendedProperties = (currentProperty, limit = 3) => {
  try {
    if (!currentProperty) return [];
    let all = [...(getPublicPropertiesDatabase().properties||[])];
    Object.keys(localStorage)
      .filter((k) => k.startsWith("imoveispro_db_user_"))
      .forEach((key) => {
        try { all = [...all, ...(JSON.parse(localStorage.getItem(key)||"{}").properties||[])]; }
        catch (_) {}
      });
    try {
      const dc = window._dataContextDebug;
      if (dc?._state?.properties) all = [...all, ...dc._state.properties];
    } catch (_) {}
    const seen = new Set();
    all = all.filter((p) => { if (!p?.id||seen.has(p.id)) return false; seen.add(p.id); return true; });
    return all
      .filter((p) => p.id !== currentProperty.id && p.status !== "Vendido")
      .sort((a,b) => new Date(b.createdAt||b.publishedAt||0) - new Date(a.createdAt||a.publishedAt||0))
      .slice(0,limit);
  } catch (_) { return []; }
};

export const getPublicProperties = () => {
  let all = [...(getPublicPropertiesDatabase().properties||[])];
  Object.keys(localStorage)
    .filter((k) => k.startsWith("imoveispro_db_user_"))
    .forEach((key) => {
      try { all = [...all, ...(JSON.parse(localStorage.getItem(key)||"{}").properties||[])]; }
      catch (_) {}
    });
  try {
    const dc = window._dataContextDebug;
    if (dc?._state?.properties) all = [...all, ...dc._state.properties];
  } catch (_) {}
  const seen = new Set();
  return all.filter((p) => { if (!p?.id||seen.has(p.id)) return false; seen.add(p.id); return true; });
};

// ============================================================
// 🏭  FACTORY / INSTÂNCIAS
// ============================================================

export const initDatabase = (userId, companyId = null) => {
  if (!userId) throw new Error("initDatabase: userId é obrigatório");
  if (databaseInstances[userId]) return databaseInstances[userId];
  const instance = new DatabaseService(userId, companyId);
  databaseInstances[userId] = instance;
  return instance;
};

export const getDatabaseInstance = () => {
  const authUser = JSON.parse(localStorage.getItem("auth_user")||"null");
  if (!authUser?.id) throw new Error("Nenhum usuário autenticado");
  if (!databaseInstances[authUser.id]) return initDatabase(authUser.id, authUser.company);
  return databaseInstances[authUser.id];
};

// ============================================================
// 🐛  DEBUG / LIMPEZA
// ============================================================

export const debugDatabaseState = () => {
  const authUser = JSON.parse(localStorage.getItem("auth_user")||"null");
  console.log("=== DEBUG DATABASE ===");
  console.log("Usuário autenticado:", authUser);
  console.log("Usuários no sistema:", getUsersDatabase().users);
  if (authUser) {
    const key = `imoveispro_db_user_${authUser.id}`;
    const raw = localStorage.getItem(key);
    console.log(`Database do usuário ${authUser.id}:`, raw ? JSON.parse(raw) : "Não existe");
  }
  console.log("Chaves imoveispro no localStorage:");
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.includes("imoveispro")) console.log(`  — ${k}`);
  }
};

export const cleanupTestData = () => {
  // ✅ Iterar de trás para frente para evitar skip ao remover chaves
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("imoveispro_db_user_") || key?.startsWith("imoveispro_db_company_"))
      keysToRemove.push(key);
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
  console.log("✅ Dados de teste limpos, usuários mantidos");
};